/**
 * vectorSpaceScene.js — framework-agnostic 3-D vector-space explorer.
 *
 * Mounts a three.js scene into a container element and drives it from the
 * backend `GET /api/analysis/vector-space` payload (which reads the app's
 * ChromaDB embeddings — the SAME vectors semantic search uses, not a parallel
 * embedding space). Adapted from the standalone demo (`demos/vector-space-3d`)
 * to be container-scoped (no window globals / element ids) and disposable so it
 * can live inside the React app as a first-class page.
 *
 * Usage:
 *   const handle = await mountVectorSpace(rootEl, { apiBase, onStatus });
 *   ...
 *   handle.dispose();
 */
import * as THREE from 'three';
import { ConvexGeometry } from 'three/addons/geometries/ConvexGeometry.js';
import { getAuthToken, getActiveOrg } from '../services/authToken';
import { el } from '../utils/domQuery';

// Named in every SelectorError this file can raise, so a thrown message says which
// scene the missing selector belongs to (#975).
const CONTEXT = 'vectorSpaceScene';

const PANEL_HTML = `
  <canvas class="vs-scene"></canvas>
  <div class="vs-toolbar">
    <button class="vs-iconbtn" data-el="menuToggle" title="Controls" aria-label="Controls">☰</button>
    <button class="vs-iconbtn" data-el="helpToggle" title="Help" aria-label="Help">?</button>
  </div>
  <aside class="vs-panel" data-el="panel">
    <div class="vs-panel-head">
      <span class="vs-panel-title">Vector Space</span>
      <button class="vs-iconbtn vs-sm" data-el="panelClose" title="Hide" aria-label="Hide">×</button>
    </div>
    <div class="vs-group">
      <label>Layout</label>
      <select data-el="projection">
        <option value="radviz">Semantic poles (data-derived)</option>
        <option value="pca">PCA (variance)</option>
      </select>
    </div>
    <div class="vs-group">
      <label>Colour</label>
      <select data-el="colorBy">
        <option value="cluster">Discovered pole</option>
        <option value="container">Container / parent</option>
        <option value="version">Spec generation</option>
        <option value="outlier">Outlier score</option>
      </select>
    </div>
    <div class="vs-group">
      <label>Size</label>
      <select data-el="sizeBy">
        <option value="uniform">Uniform</option>
        <option value="outlier">Outlier score</option>
      </select>
    </div>
    <div class="vs-group vs-toggles">
      <label><input type="checkbox" data-el="showPoles" checked /> Show poles &amp; axes</label>
      <label><input type="checkbox" data-el="showHulls" /> Container hulls</label>
      <label><input type="checkbox" data-el="spin" /> Auto-spin</label>
    </div>
    <div class="vs-group" data-el="timeWrap">
      <label>Development over time <span data-el="verLabel" class="vs-mono"></span></label>
      <div class="vs-row">
        <button data-el="play" style="flex:0 0 38px">▶</button>
        <input type="range" data-el="timeline" min="0" max="3" step="1" value="3" style="flex:1" />
      </div>
    </div>
    <div class="vs-group vs-row">
      <button data-el="theme">☀ / ☾</button>
      <button data-el="reset">Reset</button>
    </div>
    <div data-el="legend"></div>
  </aside>
  <section class="vs-help vs-hidden" data-el="help">
    <button class="vs-iconbtn vs-sm vs-help-close" data-el="helpClose" title="Close" aria-label="Close">×</button>
    <h3>Vector Space Explorer</h3>
    <p class="vs-help-data vs-mono" data-el="embedTag"></p>
    <dl class="vs-help-list">
      <dt>Layout</dt><dd>How points are placed. <b>Semantic poles</b> = data-derived axes by meaning; <b>PCA</b> = directions of greatest variance.</dd>
      <dt>Colour</dt><dd>What a dot's colour encodes — its discovered cluster, parent container, spec generation, or outlier score.</dd>
      <dt>Size</dt><dd>Uniform, or scaled by how much of an outlier each requirement is.</dd>
      <dt>Poles &amp; axes</dt><dd>The labelled cluster axes. <b>Zoom / fly in</b> to reveal finer, named <b>sub-axes</b> pointing at each sub-cluster — zoom on the intent.</dd>
      <dt>Container hulls</dt><dd>A translucent hull around each current group.</dd>
      <dt>By container (fit)</dt><dd>Re-axes the space so each <b>container</b> is an anchor; a requirement that lands by another container's anchor — or the <b>Container fit (misfiled?)</b> colour showing it red — is probably filed in the wrong place.</dd>
      <dt>Over time</dt><dd>Play or scrub how the set grew across spec generations.</dd>
    </dl>
    <div class="vs-help-gestures">
      <b>Desktop</b> drag orbit · wheel zoom · right/shift-drag pan · WASD fly · hover peek · click inspect<br/>
      <b>Touch</b> one-finger orbit · pinch zoom · two-finger pan · tap inspect
    </div>
  </section>
  <div class="vs-tooltip vs-hidden" data-el="tooltip"></div>
  <section class="vs-details vs-hidden" data-el="details">
    <button class="vs-iconbtn vs-sm" data-el="detailsClose" title="Close">×</button><div data-el="detailsBody"></div>
  </section>
  <div class="vs-stat vs-mono" data-el="stat"></div>
`;

/**
 * @param {HTMLElement} root
 * @param {{ apiBase?: string, data?: unknown, projectId?: string,
 *           onStatus?: (message: string) => void }} [options]
 */
export async function mountVectorSpace(root, { apiBase, data, projectId, onStatus } = {}) {
  const status = (m) => { try { if (onStatus) onStatus(m); } catch { /* ignore */ } };
  status('loading');

  let D;
  if (data) {
    D = data;                                   // inline (standalone HTML)
  } else {
    try {
      // Attach auth: the core API requires a bearer token + active-org header
      // (a bare fetch 401s under real auth). Prefer the in-memory token, but fall
      // back to localStorage — this scene's effect can run before AuthContext has
      // populated the module state (child effects fire before the provider's).
      /** @type {Record<string, string>} */
      const headers = {};
      const ls = (k) => { try { return localStorage.getItem(k); } catch { return null; } };
      const tok = getAuthToken() || ls('rmdv_token');
      const org = getActiveOrg() || ls('rmdv_org');
      if (tok) headers.Authorization = `Bearer ${tok}`;
      if (org) headers['X-Org-Id'] = org;
      // #261/#262: the backend now REQUIRES a project scope (400s without one) —
      // VectorSpace.js never calls mountVectorSpace without a real projectId.
      if (projectId) headers['X-Project-Id'] = projectId;
      const res = await fetch(`${apiBase}/analysis/vector-space`, { headers });
      if (!res.ok) {
        const txt = await res.text().catch(() => '');
        throw new Error(`HTTP ${res.status} ${txt.slice(0, 160)}`);
      }
      D = (await res.json()).data;
    } catch (e) {
      status('error');
      const msg = ((e && e.message) || '').toLowerCase();
      const notEnough = /at least|non-container|\b3\b|empty/.test(msg);
      const title = notEnough ? 'Not enough to map yet' : 'Couldn’t load the semantic map';
      const body = notEnough
        ? 'Add at least 3 requirements (not containers) to see the semantic map of your project.'
        : 'Something went wrong building the view. Please try again in a moment.';
      root.innerHTML =
        `<div style="height:100%;display:flex;align-items:center;justify-content:center;padding:32px">` +
          `<div style="max-width:360px;text-align:center;color:#6b7280">` +
            `<svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" ` +
              `stroke-linecap="round" stroke-linejoin="round" style="opacity:.5;margin-bottom:12px">` +
              `<circle cx="12" cy="12" r="9"/><path d="M9 10h.01M15 10h.01M9 15c.8-.8 2-1.2 3-1.2s2.2.4 3 1.2"/></svg>` +
            `<div style="font-size:16px;font-weight:600;color:var(--vs-title,#1a1f29);margin-bottom:6px">${title}</div>` +
            `<div style="font-size:13px;line-height:1.5;color:var(--vs-muted,#6b7280)">${body}</div>` +
          `</div>` +
        `</div>`;
      return { dispose() { root.innerHTML = ''; } };
    }
  }

  root.classList.add('vs-root');
  root.innerHTML = PANEL_HTML;
  // #975: every query below goes through `el`, which throws a SelectorError naming the
  // selector when nothing matches and asserts the element type before returning. The
  // three wrappers differ only in the type they assert, so a `[data-el]` node that
  // becomes the wrong kind of control is caught here instead of reading `undefined`.
  const dataEl = (name) => `[data-el="${name}"]`;
  const ui = (name) => el(root, dataEl(name), HTMLElement, CONTEXT);
  const uiInput = (name) => el(root, dataEl(name), HTMLInputElement, CONTEXT);
  const uiSelect = (name) => el(root, dataEl(name), HTMLSelectElement, CONTEXT);
  const canvas = el(root, '.vs-scene', HTMLCanvasElement, CONTEXT);

  const N = D.count, SCALE = 2.6;
  ui('embedTag').textContent = D.embeddingModel;
  ui('stat').textContent =
    `${N} requirements · ${D.poles.length} poles · ${D.groups.length} containers`;
  // hide the time control if the data has no version spread
  if (!D.version || new Set(D.version).size < 2) ui('timeWrap').style.display = 'none';

  const W = () => root.clientWidth || 1, H = () => root.clientHeight || 1;
  const isNarrow = () => W() < 640;
  root.classList.toggle('vs-narrow', isNarrow());
  if (isNarrow()) root.classList.add('vs-collapsed');   // start tidy on phones

  // palettes
  const hsl = (i, n, s = 70, l = 58) =>
    new THREE.Color(`hsl(${Math.round(360 * i / n + 12)}, ${s}%, ${l}%)`);
  const verCol = [new THREE.Color(0x3b6fd4), new THREE.Color(0x21c1a6),
    new THREE.Color(0xf2b134), new THREE.Color(0xe0518f)];
  const groupCol = D.groups.map((_, i) => hsl(i, D.groups.length));
  const clusterCol = D.poles.map((_, i) => hsl(i, D.poles.length, 72, 60));
  const ramp = (v) => new THREE.Color().setHSL((1 - v) * 0.62, 0.85, 0.55);
  const WHITE = new THREE.Color(0xffffff);
  const hasContainerFit = !!(D.layouts && D.layouts.container && D.misfiled);
  const misCol = (i) => D.misfiled[i]
    ? new THREE.Color(0xff5566)                                   // likely misfiled → red
    : new THREE.Color().setHSL(0.34, 0.55, 0.30 + 0.32 * (D.fit ? D.fit[i] : 1));  // green by fit
  const catColor = {
    version: (i) => verCol[D.version[i]] || verCol[0],
    cluster: (i) => clusterCol[D.cluster[i]],
    container: (i) => groupCol[D.group[i]],
    outlier: (i) => ramp(D.outlier[i]),
    misfiled: misCol,
  };
  const catKey = {
    version: (i) => D.version[i], cluster: (i) => D.cluster[i],
    container: (i) => D.group[i], misfiled: (i) => (D.misfiled[i] ? 1 : 0),
  };

  // alternate axis modes / colours (only if the payload carries them)
  if (hasContainerFit) {
    uiSelect('projection').insertAdjacentHTML('beforeend',
      '<option value="container">By container (fit)</option>');
    uiSelect('colorBy').insertAdjacentHTML('beforeend',
      '<option value="misfiled">Container fit (misfiled?)</option>');
  }

  // buffers
  const posR = Float32Array.from(D.radviz, (v) => v * SCALE);
  const posP = Float32Array.from(D.pca, (v) => v * SCALE);
  const posC = hasContainerFit
    ? Float32Array.from(D.layouts.container.pos, (v) => v * SCALE) : posR;
  const colors = new Float32Array(N * 3), sizes = new Float32Array(N),
    alphas = new Float32Array(N).fill(1), picks = new Float32Array(N * 3);
  for (let i = 0; i < N; i++) {
    picks[i * 3] = (i & 255) / 255;
    picks[i * 3 + 1] = ((i >> 8) & 255) / 255;
    picks[i * 3 + 2] = ((i >> 16) & 255) / 255;
  }

  const renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  const scene = new THREE.Scene();
  const camera = new THREE.PerspectiveCamera(52, W() / H(), 0.01, 200);

  const geom = new THREE.BufferGeometry();
  geom.setAttribute('position', new THREE.BufferAttribute(posR, 3));
  geom.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));
  geom.setAttribute('aSize', new THREE.BufferAttribute(sizes, 1));
  geom.setAttribute('aAlpha', new THREE.BufferAttribute(alphas, 1));
  geom.setAttribute('aPick', new THREE.BufferAttribute(picks, 3));
  const VERT = `attribute float aSize; attribute vec3 aColor; attribute float aAlpha; attribute vec3 aPick;
    varying vec3 vColor; varying float vAlpha; uniform float uScale; uniform bool uPick;
    void main(){ vColor = uPick?aPick:aColor; vAlpha=aAlpha; vec4 mv=modelViewMatrix*vec4(position,1.0);
    gl_PointSize=aSize*uScale/-mv.z; gl_Position=projectionMatrix*mv; }`;
  const FRAG = `varying vec3 vColor; varying float vAlpha; uniform bool uPick;
    void main(){ vec2 uv=gl_PointCoord-0.5; float d=length(uv); if(d>0.5) discard;
    if(uPick){ gl_FragColor=vec4(vColor,1.0); return; }
    float edge=smoothstep(0.5,0.42,d); float core=smoothstep(0.5,0.0,d);
    vec3 c=vColor*(0.55+0.7*core)+core*core*0.4; gl_FragColor=vec4(c, edge*vAlpha); }`;
  const mat = new THREE.ShaderMaterial({
    uniforms: { uScale: { value: 30 }, uPick: { value: false } },
    vertexShader: VERT, fragmentShader: FRAG, transparent: true,
    depthWrite: false, blending: THREE.AdditiveBlending,
  });
  const points = new THREE.Points(geom, mat);
  points.frustumCulled = false; scene.add(points);

  // poles
  const poleGroup = new THREE.Group(); scene.add(poleGroup);
  const allLabels = [];   // every label sprite, for responsive rescaling
  function labelSprite(text, hex, size = 0.3) {
    const c = document.createElement('canvas'); c.width = 512; c.height = 80;
    const x = c.getContext('2d');
    x.font = '600 26px "Segoe UI",sans-serif'; x.textBaseline = 'middle';
    x.shadowColor = 'rgba(0,0,0,.6)'; x.shadowBlur = 8;
    x.fillStyle = '#' + new THREE.Color(hex).getHexString();
    x.fillText(String(text).slice(0, 34), 8, 44);
    const t = new THREE.CanvasTexture(c); t.anisotropy = 4;
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: t, transparent: true, depthTest: false }));
    s.userData.base = size; allLabels.push(s);
    return s;
  }
  function applyLabelScale() {
    const f = isNarrow() ? 0.62 : 0.82;   // smaller base font, smaller again on phones
    for (const s of allLabels) { const b = s.userData.base; s.scale.set(b * 5 * f, b * f, 1); }
  }
  const poleEnd = [];     // world-space endpoint of each top pole axis
  const poleLabels = [];  // {mat, pos} for depth-aware fading
  D.poles.forEach((p, i) => {
    const v = new THREE.Vector3(...p.pos).multiplyScalar(SCALE); const col = clusterCol[i];
    poleEnd[i] = v.clone();
    poleGroup.add(new THREE.Line(
      new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), v]),
      new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.25 })));
    const lab = labelSprite(p.label, col.clone().lerp(new THREE.Color(0xffffff), 0.3), 0.34);
    const lp = v.clone().multiplyScalar(1.07); lab.position.copy(lp);
    poleGroup.add(lab); poleLabels.push({ mat: lab.material, pos: lp });
  });

  // fractal sub-poles: finer named axes that emanate from the end of each top
  // pole and point into their sub-clusters. Hidden until you zoom/fly in — so
  // you can "zoom on the intent", not just the point cloud.
  const subGroup = new THREE.Group(); scene.add(subGroup);
  const subItems = [];   // {lineMat, labMat, anchor}
  D.poles.forEach((p, i) => {
    (p.sub || []).forEach((sp) => {
      const target = new THREE.Vector3(...sp.pos).multiplyScalar(SCALE);
      const col = clusterCol[i].clone().lerp(new THREE.Color(0xffffff), 0.45);
      const lineMat = new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0 });
      subGroup.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([poleEnd[i], target]), lineMat));
      const lab = labelSprite(sp.label, col, 0.26);
      lab.position.copy(target); lab.material.opacity = 0; subGroup.add(lab);
      subItems.push({ lineMat, labMat: lab.material, anchor: target });
    });
  });
  // container anchors (axes = the containers) — shown only in "By container" mode
  const containerPoleGroup = new THREE.Group();
  containerPoleGroup.visible = false; scene.add(containerPoleGroup);
  const containerLabels = [];
  if (hasContainerFit) {
    D.layouts.container.poles.forEach((p, i) => {
      const v = new THREE.Vector3(...p.pos).multiplyScalar(SCALE);
      const col = groupCol[i] || new THREE.Color(0x88aaff);
      containerPoleGroup.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(), v]),
        new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.16 })));
      const lab = labelSprite(p.label, col.clone().lerp(WHITE, 0.35), 0.28);
      const lp = v.clone().multiplyScalar(1.06); lab.position.copy(lp);
      containerPoleGroup.add(lab); containerLabels.push({ mat: lab.material, pos: lp });
    });
  }
  applyLabelScale();

  // hulls
  const activeProj = () => {
    const m = uiSelect('projection').value;
    return m === 'pca' ? posP : m === 'container' ? posC : posR;
  };
  const hullGroup = new THREE.Group(); scene.add(hullGroup);
  function rebuildHulls() {
    hullGroup.clear();
    if (!uiInput('showHulls').checked) return;
    const mode = uiSelect('colorBy').value; if (mode === 'outlier' || mode === 'misfiled') return;
    const proj = activeProj();
    const key = catKey[mode], color = catColor[mode]; const groups = {};
    for (let i = 0; i < N; i++) {
      if (!present(i)) continue;
      (groups[key(i)] ||= []).push(i);
    }
    for (const g in groups) {
      // `Object.hasOwn` is ES2022 and this project's `lib` is ES2020, so the checker
      // reports TS2550 on it. `hasOwnProperty.call` is what the spec defines `hasOwn`
      // as, so the guard behaves identically (#975).
      if (!Object.prototype.hasOwnProperty.call(groups, g)) continue;
      const idx = groups[g]; if (idx.length < 5) continue;
      const pts = idx.map((i) => new THREE.Vector3(proj[i * 3], proj[i * 3 + 1], proj[i * 3 + 2]));
      try {
        const hg = new ConvexGeometry(pts); const col = color(idx[0]);
        // Explicit NormalBlending + higher opacity so hulls are visible on every
        // device (additive/very-low-opacity washed out on bright mobile screens).
        hullGroup.add(new THREE.Mesh(hg, new THREE.MeshBasicMaterial({
          color: col, transparent: true, opacity: 0.12, side: THREE.DoubleSide,
          depthWrite: false, blending: THREE.NormalBlending })));
        hullGroup.add(new THREE.LineSegments(new THREE.WireframeGeometry(hg),
          new THREE.LineBasicMaterial({ color: col, transparent: true, opacity: 0.32,
            blending: THREE.NormalBlending })));
      } catch { /* degenerate hull */ }
    }
  }

  // timeline / encoding
  let tl = 3;
  const present = (i) => (D.version ? D.version[i] <= tl : true);
  let selected = null;
  const baseSize = (i) => (uiSelect('sizeBy').value === 'outlier' ? 0.7 + 2.6 * D.outlier[i] : 1.15);
  let containerMode = false;
  function applyEncoding() {
    containerMode = uiSelect('projection').value === 'container';
    const proj = activeProj();
    geom.attributes.position.array.set(proj); geom.attributes.position.needsUpdate = true;
    const color = catColor[uiSelect('colorBy').value];
    for (let i = 0; i < N; i++) {
      const c = color(i); colors[i * 3] = c.r; colors[i * 3 + 1] = c.g; colors[i * 3 + 2] = c.b;
      sizes[i] = baseSize(i); alphas[i] = present(i) ? 0.9 : 0.0;
    }
    geom.attributes.aColor.needsUpdate = geom.attributes.aSize.needsUpdate =
      geom.attributes.aAlpha.needsUpdate = true;
    const showP = uiInput('showPoles').checked;
    poleGroup.visible = showP && !containerMode;
    containerPoleGroup.visible = showP && containerMode;
    rebuildHulls(); buildLegend();
    if (selected != null && present(selected)) select(selected);
    else { selected = null; ui('details').classList.add('vs-hidden'); }
  }
  function buildLegend() {
    const legend = ui('legend'); legend.innerHTML = '';
    const mode = uiSelect('colorBy').value; let entries;
    if (mode === 'version') entries = D.versions.map((v, i) => [v, verCol[i]]);
    else if (mode === 'cluster') entries = D.poles.map((p, i) => [p.label, clusterCol[i]]);
    else if (mode === 'outlier') entries = [['low', ramp(0)], ['high', ramp(1)]];
    else if (mode === 'misfiled') {
      const n = D.misfiled.reduce((a, b) => a + (b ? 1 : 0), 0);
      entries = [['well-placed', new THREE.Color().setHSL(0.34, 0.55, 0.5)],
        [`likely misfiled (${n})`, new THREE.Color(0xff5566)]];
    } else { legend.innerHTML = `<div class="vs-hint">${D.groups.length} containers — hover/click to identify</div>`; return; }
    entries.forEach(([name, col]) => {
      const d = document.createElement('div'); d.className = 'vs-legend-item';
      d.innerHTML = `<span class="vs-swatch" style="background:#${col.getHexString()};color:#${col.getHexString()}"></span>${esc(name)}`;
      legend.appendChild(d);
    });
  }
  function select(i) {
    if (!present(i)) return;
    selected = i;
    for (let k = 0; k < N; k++) alphas[k] = !present(k) ? 0 : k === i ? 1 : 0.12;
    sizes[i] = baseSize(i) * 2.4;
    geom.attributes.aAlpha.needsUpdate = geom.attributes.aSize.needsUpdate = true;
    ui('detailsBody').innerHTML =
      `<div class="vs-d-id">${D.versionName ? D.versionName[i] + ' · ' : ''}outlier ${(D.outlier[i] * 100) | 0}</div>
       <h2>${esc(D.title[i])}</h2><span class="vs-chip">${esc(D.container[i])}</span>
       ${D.versionName ? `<span class="vs-chip">${esc(D.versionName[i])}</span>` : ''}
       <span class="vs-chip">pole ${D.cluster[i]}</span>
       ${hasContainerFit && D.misfiled[i] ? '<span class="vs-chip flag">possibly misfiled</span>' : ''}
       <div class="vs-d-desc">${esc(D.desc[i])}</div>`;
    ui('details').classList.remove('vs-hidden');
  }
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>]/g, (c) =>
    ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c]));

  // picking
  const pickRT = new THREE.WebGLRenderTarget(1, 1);
  const resizePick = () => pickRT.setSize(W() * renderer.getPixelRatio(), H() * renderer.getPixelRatio());
  const pb = new Uint8Array(4);
  function pickAt(mx, my) {
    mat.uniforms.uPick.value = true; mat.blending = THREE.NoBlending; mat.needsUpdate = true;
    const pv = poleGroup.visible, hv = hullGroup.visible, sv = subGroup.visible,
      cv = containerPoleGroup.visible;
    poleGroup.visible = false; hullGroup.visible = false; subGroup.visible = false;
    containerPoleGroup.visible = false;
    renderer.setRenderTarget(pickRT); renderer.clear(); renderer.render(scene, camera);
    const dpr = renderer.getPixelRatio();
    renderer.readRenderTargetPixels(pickRT, mx * dpr, pickRT.height - my * dpr, 1, 1, pb);
    renderer.setRenderTarget(null); mat.uniforms.uPick.value = false;
    mat.blending = themeAdditive ? THREE.AdditiveBlending : THREE.NormalBlending; mat.needsUpdate = true;
    poleGroup.visible = pv; hullGroup.visible = hv; subGroup.visible = sv;
    containerPoleGroup.visible = cv;
    const id = pb[0] + (pb[1] << 8) + (pb[2] << 16);
    return id < N && present(id) && (pb[0] || pb[1] || pb[2]) ? id : null;
  }
  const localXY = (e) => {
    const r = canvas.getBoundingClientRect();
    return [e.clientX - r.left, e.clientY - r.top];
  };

  // ---- event wiring (tracked for dispose) ----
  const listeners = [];
  const on = (target, ev, fn, opts) => { target.addEventListener(ev, fn, opts); listeners.push([target, ev, fn, opts]); };

  let hoverT = 0;
  on(canvas, 'pointermove', (e) => {
    if (dragging) return;
    if (performance.now() - hoverT < 40) return; hoverT = performance.now();
    const [mx, my] = localXY(e); const i = pickAt(mx, my); const tip = ui('tooltip');
    if (i != null) {
      tip.innerHTML = `<span class="vs-t-id">${esc(D.title[i])}</span><div class="vs-t-meta">${esc(D.container[i])}${D.versionName ? ' · ' + esc(D.versionName[i]) : ''}</div>`;
      tip.style.left = mx + 'px'; tip.style.top = my + 'px';
      tip.classList.remove('vs-hidden'); canvas.style.cursor = 'pointer';
    } else { tip.classList.add('vs-hidden'); canvas.style.cursor = 'grab'; }
  });
  on(canvas, 'click', (e) => { const [mx, my] = localXY(e); const i = pickAt(mx, my); if (i != null) select(i); });

  // orbit / fly / pinch — unified pointer handling (mouse + multi-touch)
  let radius = 6, theta = 0.7, phi = 1.05, dragging = false;
  const targetV = new THREE.Vector3();
  const pointers = new Map();   // pointerId -> {x, y, pan}
  let pinchDist = 0, lastMidX = 0, lastMidY = 0;
  canvas.style.touchAction = 'none';   // we own all gestures
  const panBy = (dx, dy) => {
    const r = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 0),
      u = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 1);
    targetV.addScaledVector(r, -dx * 0.003 * radius).addScaledVector(u, dy * 0.003 * radius);
  };
  on(canvas, 'pointerdown', (e) => {
    if (canvas.setPointerCapture) try { canvas.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    pointers.set(e.pointerId, { x: e.clientX, y: e.clientY, pan: e.button === 2 || e.shiftKey });
    dragging = true;
    if (pointers.size === 2) {
      const [a, b] = [...pointers.values()];
      pinchDist = Math.hypot(a.x - b.x, a.y - b.y);
      lastMidX = (a.x + b.x) / 2; lastMidY = (a.y + b.y) / 2;
    }
  });
  const endPointer = (e) => {
    pointers.delete(e.pointerId);
    if (pointers.size < 2) pinchDist = 0;
    if (pointers.size === 0) dragging = false;
  };
  on(window, 'pointerup', endPointer);
  on(window, 'pointercancel', endPointer);
  on(window, 'pointermove', (e) => {
    const p = pointers.get(e.pointerId); if (!p) return;
    const dx = e.clientX - p.x, dy = e.clientY - p.y; p.x = e.clientX; p.y = e.clientY;
    if (pointers.size >= 2) {                      // two-finger: pinch-zoom + pan
      const [a, b] = [...pointers.values()];
      const dist = Math.hypot(a.x - b.x, a.y - b.y);
      if (pinchDist) radius = Math.max(0.4, Math.min(40, radius * pinchDist / (dist || 1)));
      pinchDist = dist;
      const mx = (a.x + b.x) / 2, my = (a.y + b.y) / 2;
      panBy(mx - lastMidX, my - lastMidY); lastMidX = mx; lastMidY = my;
    } else if (p.pan) { panBy(dx, dy); }            // right/shift-drag: pan
    else { theta -= dx * 0.005; phi = Math.max(0.05, Math.min(Math.PI - 0.05, phi - dy * 0.005)); }
  });
  on(canvas, 'wheel', (e) => {
    e.preventDefault(); radius = Math.max(0.5, Math.min(40, radius * (1 + Math.sign(e.deltaY) * 0.08)));
  }, { passive: false });
  on(canvas, 'contextmenu', (e) => e.preventDefault());
  const keys = {};
  on(window, 'keydown', (e) => { keys[e.key.toLowerCase()] = true; });
  on(window, 'keyup', (e) => { keys[e.key.toLowerCase()] = false; });

  // controls
  const verNames = (D.versions && D.versions.length) ? D.versions : ['V1', 'V2', 'V3', 'V4'];
  function setTimeline(v) {
    tl = +v; ui('verLabel').textContent = `≤ ${verNames[tl] || ''}`;
    uiInput('timeline').value = v; applyEncoding();
  }
  // Reads the range input directly instead of `e.target`, which is an `EventTarget` and
  // carries no `.value`. The handler is bound to that one input, so `e.target` was
  // always this element and the value is already committed when `input` fires (#975).
  uiInput('timeline').oninput = () => setTimeline(uiInput('timeline').value);
  let playing = null;
  ui('play').onclick = () => {
    if (playing) { clearInterval(playing); playing = null; ui('play').textContent = '▶'; return; }
    ui('play').textContent = '⏸'; let v = 0; setTimeline(0);
    playing = setInterval(() => {
      v++; if (v > 3) { clearInterval(playing); playing = null; ui('play').textContent = '▶'; return; }
      setTimeline(v);
    }, 1100);
  };
  let themeAdditive = true;
  ui('theme').onclick = () => {
    root.classList.toggle('vs-theme-light');
    themeAdditive = !root.classList.contains('vs-theme-light');
    mat.blending = themeAdditive ? THREE.AdditiveBlending : THREE.NormalBlending; mat.needsUpdate = true;
  };
  ui('reset').onclick = () => {
    radius = 6; theta = 0.7; phi = 1.05; targetV.set(0, 0, 0);
    selected = null; ui('details').classList.add('vs-hidden'); applyEncoding();
  };
  ['projection', 'colorBy', 'sizeBy'].forEach((id) => { uiSelect(id).onchange = applyEncoding; });
  uiInput('showPoles').onchange = () => {
    const s = uiInput('showPoles').checked;
    poleGroup.visible = s && !containerMode;
    containerPoleGroup.visible = s && containerMode;
  };
  uiInput('showHulls').onchange = rebuildHulls;
  ui('detailsClose').onclick = () => { selected = null; ui('details').classList.add('vs-hidden'); applyEncoding(); };

  // chrome: collapse the controls / toggle help (reclaim screen space)
  const closeHelp = () => ui('help').classList.add('vs-hidden');
  ui('menuToggle').onclick = () => { root.classList.toggle('vs-collapsed'); closeHelp(); };
  ui('panelClose').onclick = () => root.classList.add('vs-collapsed');
  ui('helpToggle').onclick = () => ui('help').classList.toggle('vs-hidden');
  ui('helpClose').onclick = closeHelp;

  function resize() {
    camera.aspect = W() / H(); camera.updateProjectionMatrix();
    renderer.setSize(W(), H(), false); resizePick();
    root.classList.toggle('vs-narrow', isNarrow());   // responsive / mobile panels
    applyLabelScale();
  }
  const ro = new ResizeObserver(resize); ro.observe(root);
  on(window, 'resize', resize); resize();

  // Depth-aware labels: near axes read clearly, far ones recede, and the giant
  // label you'd otherwise get right in front of the camera is faded out. Fractal
  // sub-axes additionally fade IN as you dolly/fly toward their cluster.
  const smooth = (a, b, x) => { const t = Math.min(1, Math.max(0, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
  function updateLabels() {
    const cam = camera.position;
    const zoomIn = smooth(4.4, 2.2, radius);          // 0 zoomed-out → 1 zoomed-in
    if (containerMode) {
      // many container anchors: show only the nearer ones, depth-faded
      for (const cl of containerLabels) {
        const d = cl.pos.distanceTo(cam);
        cl.mat.opacity = smooth(0.7, 1.8, d) * (1 - smooth(7, 13, d));
      }
      subGroup.visible = false;
      return;
    }
    // top-level poles: full at overview, fade as you dive in (sub-axes take over),
    // and fade the one that's too close (huge) or too far (clutter).
    for (const pl of poleLabels) {
      const d = pl.pos.distanceTo(cam);
      const near = smooth(0.7, 1.8, d);                // 0 when right in your face
      const far = 1 - smooth(9, 15, d);                // recede far away
      pl.mat.opacity = (1 - 0.7 * zoomIn) * near * far;
    }
    // fractal sub-axes
    let any = false;
    for (const s of subItems) {
      const dc = s.anchor.distanceTo(cam);
      const prox = smooth(3.0, 0.5, s.anchor.distanceTo(targetV));
      const near = smooth(0.35, 1.0, dc);              // near = the one you're flying into
      const far = 1 - smooth(5.5, 9, dc);
      const o = zoomIn * prox * near * far;
      s.lineMat.opacity = 0.5 * o; s.labMat.opacity = o;
      if (o > 0.02) any = true;
    }
    subGroup.visible = any;
  }

  let raf = 0, disposed = false;
  function animate() {
    if (disposed) return;
    raf = requestAnimationFrame(animate);
    if (uiInput('spin').checked) theta += 0.0015;
    const sp = 0.04 * radius;
    if (keys.w || keys.a || keys.s || keys.d) {
      const f = new THREE.Vector3(); camera.getWorldDirection(f); f.y = 0; f.normalize();
      const r = new THREE.Vector3().setFromMatrixColumn(camera.matrix, 0);
      if (keys.w) targetV.addScaledVector(f, sp); if (keys.s) targetV.addScaledVector(f, -sp);
      if (keys.a) targetV.addScaledVector(r, -sp); if (keys.d) targetV.addScaledVector(r, sp);
    }
    camera.position.set(
      targetV.x + radius * Math.sin(phi) * Math.cos(theta),
      targetV.y + radius * Math.cos(phi),
      targetV.z + radius * Math.sin(phi) * Math.sin(theta));
    camera.lookAt(targetV);
    updateLabels();
    renderer.render(scene, camera);
  }
  setTimeline(3); animate();
  status('ready');

  return {
    dispose() {
      disposed = true;
      if (raf) cancelAnimationFrame(raf);
      if (playing) clearInterval(playing);
      ro.disconnect();
      listeners.forEach(([t, ev, fn, opts]) => t.removeEventListener(ev, fn, opts));
      try { geom.dispose(); mat.dispose(); pickRT.dispose(); renderer.dispose(); } catch { /* ignore */ }
      root.innerHTML = ''; root.classList.remove('vs-root', 'vs-theme-light');
    },
  };
}
